import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from "@angular/core";
import { NzTooltipModule } from "ng-zorro-antd/tooltip";
import { ConfirmService } from "src/app/services/confirm.service";
import {
  AutonomousBatchStage,
  JobCancelKind,
  JobCancelResult,
  JobCancelService,
  jobCancelBatchConfirmCopy,
  jobCancelConfirmCopy,
} from "src/app/services/job-cancel.service";
import { uniquePositiveJobIds } from "src/app/services/job-status.util";

@Component({
  selector: "app-job-cancel-button",
  standalone: true,
  imports: [CommonModule, NzTooltipModule],
  template: `
    @if (canShow) {
      <button
        type="button"
        class="job-cancel-btn"
        [class.job-cancel-btn--busy]="busy"
        [disabled]="busy || disabled"
        [nz-tooltip]="tooltip"
        (click)="onClick($event)"
      >
        {{ busy ? busyLabel : label }}
      </button>
    }
  `,
  styles: [
    `
      :host {
        display: inline-flex;
      }
      .job-cancel-btn {
        appearance: none;
        background: rgba(0, 0, 0, 0.35);
        border: 1px solid rgba(255, 255, 255, 0.22);
        color: var(--interface-dim, #b8c0cc);
        font-size: 0.68rem;
        font-weight: 500;
        letter-spacing: 0.01em;
        line-height: 1.2;
        padding: 4px 10px;
        border-radius: 999px;
        cursor: pointer;
        white-space: nowrap;
      }
      .job-cancel-btn:hover:not(:disabled) {
        color: #fff;
        border-color: rgba(255, 255, 255, 0.45);
        background: rgba(0, 0, 0, 0.5);
      }
      .job-cancel-btn:disabled {
        opacity: 0.65;
        cursor: default;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobCancelButtonComponent {
  private readonly jobCancel = inject(JobCancelService);
  private readonly confirmService = inject(ConfirmService);
  private readonly cdr = inject(ChangeDetectorRef);

  @Input() jobId: number | string | (number | string)[] | null = null;
  @Input() kind: JobCancelKind = "llm";
  @Input() disabled = false;
  @Input() label = "Cancel";
  @Input() busyLabel = "Cancelling…";
  @Input() tooltip = "Stop this generation";
  @Input() confirmTitle = "";
  @Input() confirmContent = "";
  @Input() confirmOkText = "";
  @Input() confirmKeepText = "";
  /** When set with `videoId`, Cancel calls cancel-batch instead of looping job-cancel. */
  @Input() batchStage: AutonomousBatchStage | string | null = null;
  @Input() videoId: number | string | null = null;
  @Input() requestId: number | string | null = null;

  @Output() cancelStarted = new EventEmitter<number[]>();
  @Output() cancelled = new EventEmitter<JobCancelResult>();

  busy = false;

  get resolvedIds(): number[] {
    if (this.jobId == null) return [];
    return Array.isArray(this.jobId)
      ? uniquePositiveJobIds(this.jobId)
      : uniquePositiveJobIds([this.jobId]);
  }

  get canCancelBatch(): boolean {
    const videoId = Number(this.videoId);
    return (
      !!String(this.batchStage ?? "").trim() &&
      Number.isFinite(videoId) &&
      videoId > 0
    );
  }

  get canShow(): boolean {
    return this.canCancelBatch || this.resolvedIds.length > 0;
  }

  onClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.busy || this.disabled) return;
    const ids = this.resolvedIds;
    if (!this.canCancelBatch && !ids.length) return;

    const copy = this.canCancelBatch
      ? jobCancelBatchConfirmCopy()
      : jobCancelConfirmCopy(this.kind, ids.length);
    this.confirmService
      .confirm(
        this.confirmTitle || copy.title,
        this.confirmContent || copy.content,
        this.confirmOkText || copy.okText,
        this.confirmKeepText || copy.cancelText,
        "cancel",
      )
      .subscribe((confirmed) => {
        if (!confirmed) return;
        this.runCancel(ids);
      });
  }

  private runCancel(ids: number[]): void {
    this.busy = true;
    this.cdr.markForCheck();
    this.cancelStarted.emit(ids);

    const request$ = this.canCancelBatch
      ? this.jobCancel.cancelAutonomousBatch({
          video_id: this.videoId as number | string,
          stage: String(this.batchStage),
          ...(this.requestId != null && this.requestId !== ""
            ? { request_id: this.requestId }
            : {}),
        })
      : this.jobCancel.cancel(this.kind, ids);

    request$.subscribe({
      next: (result) => {
        this.busy = false;
        this.cdr.markForCheck();
        this.cancelled.emit(result);
      },
      error: () => {
        this.busy = false;
        this.cdr.markForCheck();
      },
    });
  }
}
